import _ from 'lodash'
import Vue from "vue";
import VueRouter, { Route, RouteConfig, RouterOptions } from "vue-router";
import RouterProxyComponent from "../components/RouterProxyComponent.vue";
import { Store } from "vuex";
Vue.use(VueRouter);
import { routes as SpotCheckRoutes } from '../spot_check/router'
import { themeInit, themeRemoveLight } from '@/utils/theme';
import { makeSensorNetworkingRouter, SensorNetworkingRouteNames } from './sensorNetworking';
import { autoLogin, autoLoginUser } from '@/utils/global';
import { gVm } from '@/global';
import { E_MenuManageType } from '@/views/customerManagement/menuManagement/menuTypes';
import { E_RouterNames } from './types';
import { isJDWork } from '@/custom/jdworks';
import { cleanQueryAndReload, headerQueryAccount } from '@/utils/auth';
import { makeAlarmCenterRouter } from './alarmCenterRouter';
import { firstMenuRouterName } from '@/store';
import { E_PlatformPortalEntry } from '@/api/moudules/platformCompany.types';

const FaultAnalysisMenuRoutes = [
    E_RouterNames.faultAnalysisDefault,
    E_RouterNames.modelAlarmReport,
    E_RouterNames.aiAlarmReport,
    E_RouterNames.alarmReport,
    E_RouterNames.FaultCaseLibrary,
]

const DataSearchMenuRoutes = [
    E_RouterNames.weeklyReport,
    E_RouterNames.realtimeData,
    E_RouterNames.historyData,
    E_RouterNames.faultTagLibrary,
]

const AlarmCenterMenuRoutes = [
    E_RouterNames.TempVibAlarm,
    E_RouterNames.gasAlarm,
    E_RouterNames.gdsAlarm,
    E_RouterNames.BatteryAlarm,
    E_RouterNames.AngleAlarm,
    E_RouterNames.OfflineAlarm,
    E_RouterNames.MailPush,
    E_RouterNames.WechatPush,
    E_RouterNames.VoicePush,
]

const ConfigureManagementMenuRoutes = [
    E_RouterNames.subCompanyManagement,
    E_RouterNames.modelManagement,
    E_RouterNames.equipmentManagement,
    E_RouterNames.sensorManagementNew,
    E_RouterNames.bearingManagement,
    E_RouterNames.gatewayManagement,
    E_RouterNames.sensorList,
    E_RouterNames.alarmLampManagement,
    E_RouterNames.collectCardManagement,
    E_RouterNames.gatewaySetting,
    E_RouterNames.thresholdManagement,
    E_RouterNames.sensorThresholdManagement,
    E_RouterNames.gasThresholdManagement,
    E_RouterNames.gdsThresholdManagement,
    E_RouterNames.equipmentRunThreshold,
    E_RouterNames.temperatureCompensation,
]

const SensorNetworkingMenuRoutes = [
    SensorNetworkingRouteNames.DeviceList,
    SensorNetworkingRouteNames.Eigenvalue,
    SensorNetworkingRouteNames.WaveSpectrum__MultiSpectrum,
    SensorNetworkingRouteNames.AlarmReport,
]

function makeMenuRedirect(store: Store<any>, menuRouteNames: string[], fallbackRouteName: string) {
    const routeNameSet = new Set(menuRouteNames)
    return () => {
        return {
            name: firstMenuRouterName(store.state.menus, routeNameSet) || fallbackRouteName
        }
    }
}

function makeRoutes(store: Store<any>) {
    const routes: RouteConfig[] = [
        {
            path: '/admin/alarmCenter',
            name: E_RouterNames.adminAlarmCenter,
            component: RouterProxyComponent,
            redirect: { name: E_RouterNames.adminMonitorAlarm },
            children: [
                {
                    path: 'monitor',
                    name: E_RouterNames.adminMonitorAlarm,
                    meta: { keepAlive: false, parentName: E_RouterNames.adminAlarmCenter },
                    component: () => import('../views/alarmStatistics/AlarmStatistics.vue'),
                },
                {
                    path: 'sensor',
                    name: E_RouterNames.adminSensorAlarm,
                    meta: { keepAlive: false, parentName: E_RouterNames.adminAlarmCenter },
                    component: () => import('../views/alarmStatistics/SensorAlarm.vue'),
                },
                {
                    path: 'trend',
                    name: E_RouterNames.adminAlarmTrend,
                    meta: { keepAlive: false, parentName: E_RouterNames.adminAlarmCenter },
                    component: () => import('../views/online/OnlinePoint.vue'),
                },
                {
                    path: 'spectrum',
                    name: E_RouterNames.adminAlarmSpectrum,
                    meta: { keepAlive: false, parentName: E_RouterNames.adminAlarmCenter },
                    component: () => import('../views/online/WaveRecord.vue'),
                },
            ],
        },
        {
            path: "/",
            redirect: {
                name: E_RouterNames.login
            },
        },
        {
            path: "/largeScreenCompany",
            name: E_RouterNames.largeScreenCompany,
            meta: {
                keepAlive: false,
                isBack: false,
                noMainLayoutComponent: true,
                keepDark: true,
            },
            component: () =>
                import(
        /* webpackChunkName: "largeScreenCompany" */ "../views/screen/v1/LargeScreenCompany/LargeScreenCompany.vue"
                ),
        },
        {
            path: "/largeScreen",
            name: E_RouterNames.largeScreenNormal,
            redirect(to) {
                if (isJDWork(store)) {
                    return {
                        name: E_RouterNames.largeScreenLegacyNormal,
                    }
                } else {
                    return {
                        name: E_RouterNames.largeScreen2,
                    }
                }
            },
        },
        {
            path: "/largeScreen2",
            name: E_RouterNames.largeScreen2,
            meta: {
                keepAlive: false,
                isBack: false,
                noMainLayoutComponent: true,
                keepDark: true,
            },
            component: () =>
                import(
        /* webpackChunkName: "largeScreen2" */ "../views/screen/v1/LargeScreen2/LargeScreen2.vue"
                ),
        },
        {
            path: "/largeScreenLegacy",
            meta: {
                keepAlive: false,
                isBack: false,
                noMainLayoutComponent: true,
                keepDark: true,
            },
            component: () =>
                import(
            /* webpackChunkName: "largeScreen" */ "../views/screen/v0/LargeScreen/LargeScreen.vue"
                ),
            children: [
                {
                    path: "",
                    name: E_RouterNames.largeScreenLegacyNormal,
                    meta: {
                        keepAlive: false,
                        isBack: false,
                        noMainLayoutComponent: true,
                        keepDark: true,
                    },
                    component: () =>
                        import(
                  /* webpackChunkName: "largeScreen" */ "../views/screen/v0/LargeScreen/views/Normal.vue"
                        ),
                },
                {
                    path: "ext",
                    name: E_RouterNames.largeScreenLegacyExt,
                    meta: {
                        keepAlive: false,
                        isBack: false,
                        noMainLayoutComponent: true,
                        keepDark: true,
                        usePx: true,
                    },
                    component: () =>
                        import(
                  /* webpackChunkName: "largeScreen" */ "../views/screen/v0/LargeScreen/views/Ext.vue"
                        ),
                },
            ]
        },
        {
            path: "/largeScreenAdmin",
            name: E_RouterNames.largeScreenAdmin,
            meta: {
                keepAlive: false,
                isBack: false,
                noMainLayoutComponent: true,
                keepDark: true,
            },
            component: () =>
                import(
        /* webpackChunkName: "largeScreenAdmin" */ "../views/screen/v0/LargeScreenAdmin/LargeScreen.vue"
                ),
        },
        // 客户管理 - 租户管理
        {
            path: '/customerManagement_tenant',
            name: E_RouterNames.customerManagement_tenant,
            component: () => import(/* webpackChunkName: "tenantManagement" */ "../views/customerManagement/tenantManagement/tenantManagement.vue")
        },
        // 客户管理 - 数据稀释
        {
            path: '/dataDilution',
            name: E_RouterNames.dataDilution,
            component: () => import(/* webpackChunkName: "dataDilution" */ "../views/customerManagement/tenantManagement/views/dataDilution.vue")
        },
        // 客户管理 - OpenAPI管理
        {
            path: '/openAPI',
            name: E_RouterNames.openAPI,
            component: () => import(/* webpackChunkName: "tenantManagement" */ "../views/customerManagement/openAPIManagement/openAPIManagement.vue")
        },
        // 客户管理 - 菜单管理
        {
            meta: {
                keepAlive: false,
            },
            path: '/customerManagement_menu',
            name: E_RouterNames.customerManagement_menu,
            component: () => import(/* webpackChunkName: "menuManagement" */ "../views/customerManagement/menuManagement/menuManagement.vue")
        },
        // 客户管理 - App菜单管理
        {
            meta: {
                keepAlive: false,
            },
            path: '/customerManagement_appMenu',
            name: E_RouterNames.customerManagement_appMenu,
            component: () => import(/* webpackChunkName: "appMenuManagement" */ "../views/customerManagement/menuManagement/menuManagement.vue"),
            props: { menuManagerType: E_MenuManageType.App }
        },
        // 客户管理 - 操作日志（兼容旧路径）
        {
            path: '/customerManagement_operateLog',
            redirect: {
                name: E_RouterNames.customerManagement_operateLog
            }
        },
        // 客户管理 - 登录日志（兼容旧路径）
        {
            path: '/customerManagement_loginLog',
            redirect: {
                name: E_RouterNames.customerManagement_loginLog
            }
        },
        // 客户管理-APP版本管理
        {
            path: "/appver",
            name: E_RouterNames.appver,
            component: () =>
                import(
        /* webpackChunkName: "appver" */ "../views/customerManagement/appver/appver.vue"
                ),
        },
        // deepseek设置
        {
            path: "/deepseekSetting",
            name: E_RouterNames.deepseekSetting,
            meta: {
                keepAlive: false,
                isBack: false,
            },
            component: () =>
                import(
        /* webpackChunkName: "deepseekSetting" */ "../views/customerManagement/sysconfig/deepseekSetting.vue"
                ),
        },
        // 大屏价值指标设置
        {
            path: "/platformScreenSetting",
            name: E_RouterNames.platformScreenSetting,
            meta: {
                keepAlive: false,
                isBack: false,
            },
            component: () =>
                import(
        /* webpackChunkName: "platformScreenSetting" */ "../views/customerManagement/sysconfig/platformScreenSetting.vue"
                ),
        },
        // 更新列表 
        {
            path: "/systemNoticeList",
            name: E_RouterNames.systemNoticeList,
            meta: {
                keepAlive: false,
                isBack: false,
            },
            component: () =>
                import(
        /* webpackChunkName: "systemNoticeList" */ "../views/customerManagement/systemNotice/systemNoticeList.vue"
                ),
        },
        // 新建更新
        {
            path: "/systemNoticeCreate",
            name: E_RouterNames.systemNoticeCreate,
            meta: {
                keepAlive: false,
                isBack: false,
                parentName: E_RouterNames.systemNoticeList,
            },
            component: () =>
                import(
        /* webpackChunkName: "systemNoticeCreate" */ "../views/customerManagement/systemNotice/systemNoticeCreate.vue"
                ),
        },
        {
            path: '/firmwareUpgrad',
            name: E_RouterNames.firmwareUpgrad,
            component: () => import(/*webpackChunkName: "firmwareUpgrad"*/"../views/customerManagement/firmwareUpgrad/firmwareUpgrad.vue"),
        },
        {
            path: '/faultCaseLibraryList_root',
            name: E_RouterNames.faultCaseLibraryList_root,
            props: {
                isAdmin: true,
            },
            component: () =>
                import(
                /* webpackChunkName: "FaultCaseLibrary" */ "../views/alarm/FaultCaseLibrary.vue"
                ),
        },
        {
            path: '/faultCaseLibraryCreateEdit_root',
            name: E_RouterNames.faultCaseLibraryCreateEdit_root,
            meta: {
                parentName: E_RouterNames.faultCaseLibraryList_root,
            },
            props: {
                isAdmin: true,
            },
            component: () =>
                import(
                /* webpackChunkName: "FaultCaseLibrary" */ "../views/alarm/subviews/FaultCaseLibraryCreateEdit.vue"
                ),
        },
        {
            path: '/diagnosticResult',
            name: E_RouterNames.diagnosticResult,
            props: {
                isAdmin: true,
            },
            component: () =>
                import(
                /* webpackChunkName: "diagnosticResult" */ "../views/customerManagement/diagnosticResult/diagnosticResult.vue"
                ),
        },
        {
            path: '/diagnosticModel',
            name: E_RouterNames.diagnosticModel,
            props: {
                isAdmin: true,
            },
            component: () =>
                import(
                /* webpackChunkName: "diagnosticModel" */ "../views/customerManagement/diagnosticModel/diagnosticModel.vue"
                ),
        },

        // 系统管理-APP版本更新
        {
            path: "/appverCreate",
            name: E_RouterNames.appverCreate,
            meta: {
                parentName: E_RouterNames.appver,
            },
            component: () =>
                import(
        /* webpackChunkName: "appver" */ "../views/customerManagement/appver/AppVerCreate/AppVerCreate.vue"
                ),
        },
        // 系统管理-用户管理
        {
            path: "/userList",
            name: E_RouterNames.userList,
            meta: {
                keepAlive: false,
                isBack: false,
            },
            component: () =>
                import(
        /* webpackChunkName: "userList" */ "../views/system/user/UserList.vue"
                ),
        },
        // 系统管理-用户管理-分配角色
        {
            path: "/authRole",
            name: E_RouterNames.authRole,
            meta: {
                keepAlive: false,
                isBack: false,
                parentName: E_RouterNames.userList,
            },
            component: () =>
                import(
        /* webpackChunkName: "authRole" */ "../views/system/user/views/authRole/authRole.vue"
                ),
        },
        // 系统管理-用户管理-分配权限
        {
            path: "/authAuthority",
            name: E_RouterNames.authAuthority,
            meta: {
                keepAlive: false,
                isBack: false,
                parentName: E_RouterNames.userList,
            },
            component: () =>
                import(
        /* webpackChunkName: "authAuthority" */ "../views/system/user/views/authAuthority/authAuthority.vue"
                ),
        },
        // 系统管理-权限管理
        {
            path: '/authorityManagement',
            name: E_RouterNames.authorityManagement,
            component: () => import(/* webpackChunkName: "authorityManagement" */ "../views/system/authorityManagement/authorityManagement.vue")
        },
        // 系统管理 - 操作日志
        {
            path: '/system_operateLog',
            name: E_RouterNames.customerManagement_operateLog,
            meta: {
                companyAdminOnly: true,
            },
            component: () => import(/* webpackChunkName: "operlogManagement" */ "../views/system/operlogManagement/operlogManagement.vue")
        },
        // 系统管理 - 登录日志
        {
            path: '/system_loginLog',
            name: E_RouterNames.customerManagement_loginLog,
            meta: {
                companyAdminOnly: true,
            },
            component: () => import(/* webpackChunkName: "operlogLoginManagement" */ "../views/system/operlogLoginManagement/operlogLoginManagement.vue")
        },
        // 系统管理-用户管理-邮件模板
        {
            path: "/emailManagement",
            name: E_RouterNames.emailManagement,
            component: () =>
                import(
        /* webpackChunkName: "emailManager" */ "../views/system/email/emailManager/emailManager.vue"
                ),
        },
        // 系统管理-语音管理
        {
            path: "/voiceManagement",
            name: E_RouterNames.voiceManagement,
            component: () =>
                import(
        /* webpackChunkName: "voiceManagement" */ "../views/system/alarmVoice/AlarmVoiceManagement.vue"
                ),
        },
        // 系统管理-用户管理-邮件模板 创建、编辑
        {
            path: "/emailCreateEdit",
            name: E_RouterNames.emailCreateEdit,
            meta: {
                parentName: E_RouterNames.emailManagement,
            },
            component: () =>
                import(
        /* webpackChunkName: "emailCreateEdit" */ "../views/system/email/emailCreateEdit/emailCreateEdit.vue"
                ),
        },
        // 系统管理-角色管理
        {
            path: "/roleList",
            name: E_RouterNames.roleList,
            meta: {
                keepAlive: false,
                isBack: false,
            },
            component: () =>
                import(
        /* webpackChunkName: "roleList" */ "../views/system/role/RoleList.vue"
                ),
        },        
        {
            path: "/faultAnalysis",
            name: E_RouterNames.faultAnalysis,
            redirect: makeMenuRedirect(store, FaultAnalysisMenuRoutes, E_RouterNames.faultAnalysisDefault),
            component: RouterProxyComponent,
            children: [
                {
                    path: "aiAlarmReport",
                    name: E_RouterNames.aiAlarmReport,
                    meta: {
                        keepAlive: false,
                        isBack: false,
                    },
                    component: () =>
                        import(
                    /* webpackChunkName: "aiAlarmReport" */ "../views/alarm/AIAlarmReport.vue"
                        ),
                },
                {
                    path: "aiAlarmReportDetail",
                    name: E_RouterNames.aiAlarmReportDetail,
                    meta: {
                        keepAlive: false,
                        isBack: false,
                        parentName: E_RouterNames.aiAlarmReport,
                    },
                    component: () =>
                        import(
                    /* webpackChunkName: "aiAlarmReportDetail" */ "../views/alarm/subviews/AIAlarmReportDetail.vue"
                        ),
                },
                {
                    path: "alarmReport",
                    name: E_RouterNames.alarmReport,
                    meta: {
                        keepAlive: false,
                        isBack: false,
                    },
                    component: () =>
                        import(
            /* webpackChunkName: "alarmReport" */ "../views/alarm/AlarmReport.vue"
                        ),
                },
                // 报警记录-详情
                {
                    path: "alarmReportDetail",
                    name: E_RouterNames.alarmReportDetail,
                    meta: {
                        keepAlive: false,
                        isBack: false,
                        parentName: E_RouterNames.alarmReport,
                    },
                    component: () =>
                        import(
            /* webpackChunkName: "AlarmReportDetail" */ "../views/alarm/subviews/AlarmReportDetail.vue"
                        ),
                },
                // 故障诊断-详情
                {
                    path: "faultAnalysisDetail",
                    name: E_RouterNames.faultAnalysisDetail,
                    meta: {
                        keepAlive: false,
                        isBack: false,
                        parentName: E_RouterNames.faultAnalysisDefault
                    },
                    component: () =>
                        import(
            /* webpackChunkName: "faultAnalysisDetail" */ "../views/alarm/subviews/FaultAnalysisDetail.vue"
                        ),
                },
                // 故障诊断
                {
                    path: "",
                    name: E_RouterNames.faultAnalysisDefault,
                    meta: {
                        isBack: false,
                    },
                    component: () =>
                        import(
            /* webpackChunkName: "FaultDiagnosisRoot" */ "../views/alarm/FaultDiagnosisRoot.vue"
                        ),
                },
                // 故障诊断
                {
                    path: "modelAlarmReport",
                    name: E_RouterNames.modelAlarmReport,
                    meta: {
                        isBack: false,
                    },
                    component: () =>
                        import(
                            /* webpackChunkName: "modelAlarmReport" */ "../views/alarm/ModelAlarmReport.vue"
                        ),
                },
                // 故障诊断详情
                {
                    path: "modelAlarmReportDetail",
                    name: E_RouterNames.modelAlarmReportDetail,
                    meta: {
                        isBack: false,
                        parentName: E_RouterNames.modelAlarmReport,
                    },
                    component: () =>
                        import(
                            /* webpackChunkName: "modelAlarmReport" */ "../views/alarm/subviews/ModelAlarmReportDetail.vue"
                        ),
                },
                // 设备诊断
                {
                    path: "faultAnalysisEquipment",
                    name: E_RouterNames.faultAnalysisEquipment,
                    meta: {
                        isBack: false,
                        parentName: E_RouterNames.faultAnalysisDefault,
                    },
                    component: () =>
                        import(
            /* webpackChunkName: "faultAnalysis" */ "../views/alarm/subviews/FaultAnalysisEquipment.vue"
                        ),
                },
                // 维修
                {
                    path: "FaultRepair",
                    name: E_RouterNames.FaultRepair,
                    meta: {
                        isBack: false,
                        parentName: E_RouterNames.faultAnalysisDefault,
                    },
                    component: () =>
                        import(
                        /* webpackChunkName: "FaultRepair" */ "../views/alarm/FaultRepair.vue"
                        ),
                },
                // 保养
                {
                    path: "FaultUpkeep",
                    name: E_RouterNames.FaultUpkeep,
                    meta: {
                        isBack: false,
                        parentName: E_RouterNames.faultAnalysisDefault,
                    },
                    component: () =>
                        import(
                        /* webpackChunkName: "FaultUpkeep" */ "../views/alarm/FaultUpkeep.vue"
                        ),
                },
                // 平台处理
                {
                    path: "FaultPlatformHandleResults",
                    name: E_RouterNames.FaultPlatformHandleResults,
                    meta: {
                        isBack: false,
                        parentName: E_RouterNames.faultAnalysisDefault,
                    },
                    component: () =>
                        import(
                        /* webpackChunkName: "FaultPlatformHandleResults" */ "../views/alarm/subviews/FaultPlatformHandleResults.vue"
                        ),
                },
                {
                    path: 'FaultCaseLibrary',
                    name: E_RouterNames.FaultCaseLibrary,
                    meta: {
                        isBack: false,
                    },
                    component: () =>
                        import(
                        /* webpackChunkName: "FaultCaseLibrary" */ "../views/alarm/FaultCaseLibrary.vue"
                        ),
                },
                {
                    path: 'FaultCaseLibraryCreateEdit',
                    name: E_RouterNames.FaultCaseLibraryCreateEdit,
                    meta: {
                        isBack: false,
                        parentName: E_RouterNames.FaultCaseLibrary,
                    },
                    component: () =>
                        import(
                        /* webpackChunkName: "FaultCaseLibrary" */ "../views/alarm/subviews/FaultCaseLibraryCreateEdit.vue"
                        ),
                }
            ],
        },
        // 健康监测-首页
        {
            path: "/healthTesting",
            name: E_RouterNames.healthTesting,
            meta: {
                isBack: false,
            },
            component: () =>
                import(
        /* webpackChunkName: "healthTesting" */ "../views/health/HealthTesting.vue"
                ),
        },
        // 健康监测-设备测点评分
        {
            path: "/healthTestingEquipmentList",
            name: E_RouterNames.healthTestingEquipmentList,
            meta: {
                isBack: false,
                parentName: E_RouterNames.healthTesting,
            },
            component: () =>
                import(
        /* webpackChunkName: "HealthTestingEquipmentList" */ "../views/health/HealthTestingEquipmentList.vue"
                ),
        },
        // 健康监测-测点详细分数
        {
            path: "/healthTestingMappingDetail",
            name: E_RouterNames.healthTestingMappingDetail,
            meta: {
                isBack: false,
                parentName: E_RouterNames.healthTesting,
            },
            component: () =>
                import(
        /* webpackChunkName: "HealthTestingMappingDetail" */ "../views/health/HealthTestingMappingDetail.vue"
                ),
        },
        // 健康监测-评分训练模型
        {
            path: "/healthTestingTrainList",
            name: E_RouterNames.healthTestingTrainList,
            meta: {
                isBack: false,
                parentName: E_RouterNames.healthTesting,
            },
            component: () =>
                import(
        /* webpackChunkName: "HealthTestingTrainList" */ "../views/health/HealthTestingTrainList.vue"
                ),
        },
        // 波形频谱
        {
            path: "/waveSpectrum",
            name: E_RouterNames.waveSpectrum,
            meta: {
                keepAlive: false,
                isBack: false,
            },
            component: () =>
                import(
        /* webpackChunkName: "waveSpectrum" */ "../views/wave/WaveSpectrum.vue"
                ),
        },
        // 在线监测
        {
            path: "/onlineMonitor",
            name: E_RouterNames.onlineMonitor,
            redirect: { name: E_RouterNames.onlineMonitorDefault },
            component: RouterProxyComponent,
            meta: {
                keepAlive: false,
                isBack: false,
            },
            children: [
                {
                    path: "device",
                    name: E_RouterNames.onlineDevice,
                    meta: {
                        keepAlive: false,
                        isBack: false,
                        parentName: E_RouterNames.onlineMonitor,
                    },
                    component: () =>
                        import(
            /* webpackChunkName: "onlineDevice" */ "../views/online/OnlineDeviceNew.vue"
                        ),
                },
                {
                    path: "device_old",
                    name: E_RouterNames.onlineDeviceOld,
                    meta: {
                        keepAlive: false,
                        isBack: false,
                        parentName: E_RouterNames.onlineMonitor,
                    },
                    component: () =>
                        import(
            /* webpackChunkName: "onlineDevice" */ "../views/online/OnlineDevice.vue"
                        ),
                },
                // 在线监测-测点
                {
                    path: "point",
                    name: E_RouterNames.onlinePoint,
                    meta: {
                        keepAlive: false,
                        isBack: false,
                        parentName: E_RouterNames.onlineMonitor,
                    },
                    component: () =>
                        import(
            /* webpackChunkName: "onlinePoint" */ "../views/online/OnlinePoint.vue"
                        ),
                },
                // 在线监测-波形记录
                {
                    path: "waveRecord",
                    name: E_RouterNames.waveRecord,
                    meta: {
                        keepAlive: false,
                        isBack: false,
                        parentName: E_RouterNames.onlineMonitor,
                    },
                    component: () =>
                        import(
            /* webpackChunkName: "waveRecord" */ "../views/online/WaveRecord.vue"
                        ),
                },
                // 声纹频谱
                {
                    path: "voicePrintRecord",
                    name: E_RouterNames.voicePrintRecord,
                    meta: {
                        keepAlive: false,
                        isBack: false,
                        parentName: E_RouterNames.onlineMonitor,
                    },
                    component: () =>
                        import(
                /* webpackChunkName: "voicePrintRecord" */ "../views/online/VoicePrintRecord.vue"
                        ),
                },
                {
                    path: "old",
                    name: E_RouterNames.onlineMonitorOld,
                    meta: {
                        keepAlive: false,
                        isBack: false,
                        parentName: E_RouterNames.onlineMonitor,
                    },
                    component: () =>
                        import(
            /* webpackChunkName: "onlineMonitor" */ "../views/online/OnlineMonitor.vue"
                        ),
                },
                {
                    path: "",
                    name: E_RouterNames.onlineMonitorDefault,
                    meta: {
                        keepAlive: false,
                        isBack: false,
                        parentName: E_RouterNames.onlineMonitor,
                    },
                    component: () =>
                        import(
            /* webpackChunkName: "onlineMonitorNew" */ "../views/online/OnlineMonitorNew.vue"
                        ),
                },
                {
                    path: 'area_Points',
                    name: E_RouterNames.area_Points,
                    meta: {
                        parentName: E_RouterNames.onlineMonitor,
                    },
                    component: () => import(
                    /* webpackChunkName: "area_Points" */ "../views/online/Area_Points.vue"
                    ),
                },
                {
                    path: 'equipment_Points',
                    name: E_RouterNames.equipment_Points,
                    meta: {
                        parentName: E_RouterNames.onlineMonitor,
                    },
                    component: () => import(
                    /* webpackChunkName: "equipment_Points" */ "../views/online/Equipment_Points.vue"
                    ),
                },
            ],
        },
        //数据查询
        {
            path: '/dataSearch',
            name: E_RouterNames.dataSearch,
            redirect: makeMenuRedirect(store, DataSearchMenuRoutes, E_RouterNames.realtimeData),
            component: RouterProxyComponent,
            children: [
                // 数据查询-实时数据
                {
                    path: "realtimeData",
                    name: E_RouterNames.realtimeData,
                    meta: {
                        isBack: false,
                    },
                    component: () =>
                        import(
        /* webpackChunkName: "realtimeData" */ "../views/dataSearch/RealtimeData.vue"
                        ),
                },
                // 数据查询-历史数据
                {
                    path: "historyData",
                    name: E_RouterNames.historyData,
                    meta: {
                        isBack: false,
                    },
                    component: () =>
                        import(
        /* webpackChunkName: "historyData" */ "../views/dataSearch/HistoryData.vue"
                        ),
                },
                // 数据查询-历史测点详情
                {
                    path: "historyPoint",
                    name: E_RouterNames.historyPoint,
                    meta: {
                        isBack: false,
                        parentName: E_RouterNames.historyData,
                    },
                    component: () =>
                        import(
        /* webpackChunkName: "historyPoint" */ "../views/dataSearch/HistoryPoint.vue"
                        ),
                },
                // 数据查询-故障标签库
                {
                    path: "faultTagLibrary",
                    name: E_RouterNames.faultTagLibrary,
                    meta: {
                        isBack: false,
                    },
                    component: () =>
                        import(
        /* webpackChunkName: "faultTagLibrary" */ "../views/dataSearch/FaultTagLibrary/FaultTagLibrary.vue"
                        ),
                },
                // 数据查询-周报
                {
                    path: "weeklyReport",
                    name: E_RouterNames.weeklyReport,
                    component: () =>
                        import(
        /* webpackChunkName: "weeklyReport" */ "../views/dataSearch/weeklyReport/weeklyReport.vue"
                        ),
                },
                // 数据查询-周报编辑
                {
                    path: "weeklyReportCreateAndEdit",
                    name: E_RouterNames.weeklyReportCreateAndEdit,
                    component: () =>
                        import(
        /* webpackChunkName: "weeklyReportCreateAndEdit" */ "../views/dataSearch/weeklyReport/weeklyReportCreateAndEdit/weeklyReportCreateAndEdit.vue"
                        ),
                    meta: {
                        noMainLayoutComponent: true,
                        keepDark: true,
                        parentName: E_RouterNames.weeklyReport,
                    }
                },
                // 数据查询-周报编辑
                {
                    path: "weeklyReportCreateAndEdit2",
                    name: E_RouterNames.weeklyReportCreateAndEdit2,
                    component: () =>
                        import(
        /* webpackChunkName: "weeklyReportCreateAndEdit2" */ "../views/dataSearch/weeklyReport2/weeklyReportCreateAndEdit2.vue"
                        ),
                    meta: {
                        noMainLayoutComponent: true,
                        keepDark: true,
                        parentName: E_RouterNames.weeklyReport,
                    }
                },
            ]
        },
        ...makeAlarmCenterRouter(makeMenuRedirect(store, AlarmCenterMenuRoutes, E_RouterNames.TempVibAlarm)),
        // 配置管理
        {
            path: "/configureManagement",
            name: E_RouterNames.configureManagement,
            redirect: makeMenuRedirect(store, ConfigureManagementMenuRoutes, E_RouterNames.subCompanyManagement),
            component: RouterProxyComponent,
            children: [
                // 设备大屏
                {
                    path: "deviceScreen",
                    name: E_RouterNames.deviceScreen,
                    meta: {
                        keepAlive: false,
                        isBack: false,
                    },
                    component: () =>
                        import(
        /* webpackChunkName: "deviceScreen" */ "../views/device/DeviceScreen.vue"
                        ),
                },
                {
                    path: "subCompany",
                    name: E_RouterNames.subCompanyManagement,
                    component: () =>
                        import(
          /* webpackChunkName: "subCompanyManagement" */ "../views/configureManagement/subCompanyManagement/subCompanyManagement.vue"
                        ),
                },
                {
                    path: "uploadImage_Company",
                    name: E_RouterNames.uploadImage_Company,
                    meta: {
                        parentName: E_RouterNames.subCompanyManagement,
                    },
                    component: () =>
                        import(
          /* webpackChunkName: "subCompanyManagement" */ "../views/configureManagement/subCompanyManagement/subview/uploadImage_Comany.vue"
                        ),
                },
                {
                    path: "uploadImage_Area",
                    name: E_RouterNames.uploadImage_Area,
                    meta: {
                        parentName: E_RouterNames.subCompanyManagement,
                    },
                    component: () =>
                        import(
          /* webpackChunkName: "subCompanyManagement" */ "../views/configureManagement/subCompanyManagement/subview/uploadImage_Area.vue"
                        ),
                },
                {
                    path: "gateway",
                    name: E_RouterNames.gatewayManagement,
                    component: () =>
                        import(
          /* webpackChunkName: "gatewayManagement" */ "../views/configureManagement/gatewayManagement/gatewayManagement.vue"
                        ),
                },
                {
                    path: "gatewayDetails",
                    name: E_RouterNames.gatewayDetails,
                    meta: {
                        parentName: E_RouterNames.gatewayManagement,
                    },
                    component: () =>
                        import(
          /* webpackChunkName: "gatewayManagement" */ "../views/configureManagement/gatewayManagement/subview/GatewayDetails.vue"
                        ),
                },
                {
                    path: E_RouterNames.gatewaySetting,
                    name: E_RouterNames.gatewaySetting,
                    component: () =>
                        import(
          /* webpackChunkName: "gatewaySetting" */ "../views/configureManagement/gatewaySetting/gatewaySetting.vue"
                        ),
                },
                {
                    path: "model",
                    name: E_RouterNames.modelManagement,
                    component: () =>
                        import(
          /* webpackChunkName: "modelManagement" */ "../views/configureManagement/modelManagement/modelManagement.vue"
                        ),
                },
                {
                    path: "equipment",
                    name: E_RouterNames.equipmentManagement,
                    component: () =>
                        import(
          /* webpackChunkName: "equipmentManagement" */ "../views/configureManagement/equipmentManagement/equipmentManagement.vue"
                        ),
                },
                {
                    path: E_RouterNames.deviceLog,
                    name: E_RouterNames.deviceLog,                    
                    component: () =>
                        import(
          /* webpackChunkName: "equipmentManagement" */ "../views/configureManagement/equipmentManagement/subview/equipmentDeviceLog.vue"
                        ),
                },
                {
                    path: "uploadImage_Equipment",
                    name: E_RouterNames.uploadImage_Equipment,
                    meta: {
                        parentName: E_RouterNames.equipmentManagement,
                    },
                    component: () =>
                        import(
          /* webpackChunkName: "equipmentManagement" */ "../views/configureManagement/equipmentManagement/subview/uploadImage_Equipment.vue"
                        ),
                },
                {
                    path: "equipmentComponent",
                    name: E_RouterNames.equipmentComponentManagement,
                    meta: {
                        parentName: E_RouterNames.equipmentManagement,
                    },
                    component: () =>
                        import(
          /* webpackChunkName: "equipmentComponentManagement" */ "../views/configureManagement/equipmentComponentManagement/equipmentComponentManagement.vue"
                        ),
                },
                {
                    path: "sensorList",
                    name: E_RouterNames.sensorList,
                    component: () =>
                        import(
            /* webpackChunkName: "sensorList" */ "../views/configureManagement/sensorManagement/sensorList.vue"
                        ),
                },
                {
                    path: "sensorCreateEdit",
                    name: E_RouterNames.sensorCreateEdit,
                    meta: {
                        parentName: E_RouterNames.sensorList,
                    },
                    component: () =>
                        import(
            /* webpackChunkName: "sensorList" */ "../views/configureManagement/sensorManagement/view/SensorCreateEdit.vue"
                        ),
                },
                {
                    path: "sensorWave",
                    name: E_RouterNames.sensorWave,
                    meta: {
                        parentName: E_RouterNames.sensorList,
                    },
                    component: () =>
                        import(
            /* webpackChunkName: "sensorWave" */ "../views/configureManagement/sensorManagement/view/SensorWave.vue"
                        ),
                },
                {
                    path: "sensorNew",
                    name: E_RouterNames.sensorManagementNew,
                    component: () =>
                        import(
            /* webpackChunkName: "sensorManagementNew" */ "../views/configureManagement/sensorManagementNew/sensorManagementNew.vue"
                        ),
                },
                {
                    path: "sensorNewCreateEdit",
                    name: E_RouterNames.sensorNewCreateEdit,
                    meta: {
                        parentName: E_RouterNames.sensorManagementNew,
                    },
                    component: () =>
                        import(
            /* webpackChunkName: "sensorManagementNew" */ "../views/configureManagement/sensorManagementNew/view/sensorNewCreateEdit.vue"
                        ),
                },
                {
                    path: "sensorThresholdManagement",
                    name: E_RouterNames.sensorThresholdManagement,
                    component: () =>
                        import(
            /* webpackChunkName: "sensorThresholdManagement" */ "../views/configureManagement/thresholdSettingsGroup/sensorThresholdManagement/sensorThresholdManagement.vue"
                        ),
                },
                {
                    path: "dustThresholdManagement",
                    name: E_RouterNames.gdsThresholdManagement,
                    component: () =>
                        import(
            /* webpackChunkName: "dustThresholdManagement" */ "../views/configureManagement/thresholdSettingsGroup/dustThresholdManagement/dustThresholdManagement.vue"
                        ),
                },
                {
                    path: "gasThresholdManagement",
                    name: E_RouterNames.gasThresholdManagement,
                    component: () =>
                        import(
            /* webpackChunkName: "gasThresholdManagement" */ "../views/configureManagement/thresholdSettingsGroup/gasThresholdManagement/gasThresholdManagement.vue"
                        ),
                },


                {
                    path: "bearing",
                    name: E_RouterNames.bearingManagement,
                    component: () =>
                        import(
            /* webpackChunkName: "bearingManagement" */ "../views/configureManagement/bearingManagement/bearingManagement.vue"
                        ),
                },
                {
                    path: "threshold",
                    name: E_RouterNames.thresholdManagement,
                    component: () =>
                        import(
            /* webpackChunkName: "thresholdManagement" */ "../views/configureManagement/thresholdManagement/thresholdManagement.vue"
                        ),
                },
                {
                    path: "alarmLamp",
                    name: E_RouterNames.alarmLampManagement,
                    component: () =>
                        import(
            /* webpackChunkName: "alarmLampManagement" */ "../views/configureManagement/alarmLampManagement/alarmLampManagement.vue"
                        ),
                },
                {
                    path: "alarmLampCreateEdit",
                    name: E_RouterNames.alarmLampCreateEdit,
                    meta: {
                        parentName: E_RouterNames.alarmLampManagement,
                    },
                    component: () =>
                        import(
            /* webpackChunkName: "alarmLampManagement" */ "../views/configureManagement/alarmLampCreateEdit/alarmLampCreateEdit.vue"
                        ),
                },
                {
                    path: "collectCard",
                    name: E_RouterNames.collectCardManagement,
                    component: () =>
                        import(
            /* webpackChunkName: "collectCardManagement" */ "../views/configureManagement/collectCardManagement/collectCardManagement.vue"
                        ),
                },
                {
                    path: "collectCardCreateEdit",
                    name: E_RouterNames.collectCardCreateEdit,
                    meta: {
                        parentName: E_RouterNames.collectCardManagement,
                    },
                    component: () =>
                        import(
            /* webpackChunkName: "collectCardManagement" */ "../views/configureManagement/collectCardManagement/view/collectCardCreateEdit.vue"
                        ),
                }, {
                    path: E_RouterNames.equipmentRunThreshold,
                    name: E_RouterNames.equipmentRunThreshold,
                    meta: {},
                    component: () =>
                        import(
            /* webpackChunkName: "equipmentRunThreshold" */ "../views/configureManagement/equipmentRunThreshold/equipmentRunThreshold.vue"
                        ),
                },
                {
                    path: E_RouterNames.temperatureCompensation,
                    name: E_RouterNames.temperatureCompensation,
                    component: () =>
                        import(
            /* webpackChunkName: "temperatureCompensation" */ "../views/configureManagement/temperatureCompensation/TemperatureCompensation.vue"
                        ),
                },
                {
                    path: E_RouterNames.equipmentRunThresholdDetail,
                    name: E_RouterNames.equipmentRunThresholdDetail,
                    meta: {
                        parentName: E_RouterNames.equipmentRunThreshold,
                    },
                    component: () =>
                        import(
            /* webpackChunkName: "equipmentRunThreshold" */ "../views/configureManagement/equipmentRunThreshold/equipmentRunThresholdDetail.vue"
                        ),
                },
            ],
        },
        // 登录
        {
            path: "/portal",
            name: E_RouterNames.platformPortal,
            component: () =>
                import(/* webpackChunkName: "platformPortal" */ "../views/Portal/Portal.vue"),
            meta: {
                noMainLayoutComponent: true,
            }
        },
        // 登录
        {
            path: "/login",
            name: E_RouterNames.login,
            component: () =>
                import(/* webpackChunkName: "login" */ "../views/Login/Login.vue"),
            meta: {
                keepDark: true,
                noMainLayoutComponent: true,
            }
        },
        ...SpotCheckRoutes,
        ...makeSensorNetworkingRouter(makeMenuRedirect(store, SensorNetworkingMenuRoutes, SensorNetworkingRouteNames.DeviceList)),
        {
            path: '/largeScreen_X',
            name: E_RouterNames.largeScreen,
            redirect: {
                name: E_RouterNames.largeScreenNormal
            },
        },
        // {
        //   path: "/pointMeasure",
        //   name: "pointMeasure",
        //   redirect: "/",
        // },
        // {
        //   path: "*",
        //   redirect: {
        //     name: 'login'
        //   }
        // }
    ];
    return routes
}
/**
 * 只能超级管理员看到得
 */
const SuperAdminRouterNames = [
    E_RouterNames.customerManagement_tenant,
    E_RouterNames.adminAlarmCenter,
    E_RouterNames.adminMonitorAlarm,
    E_RouterNames.adminSensorAlarm,
    E_RouterNames.adminAlarmTrend,
    E_RouterNames.adminAlarmSpectrum,
    E_RouterNames.customerManagement_menu,
    E_RouterNames.customerManagement_appMenu,
    E_RouterNames.customerManagement_dictList,
    E_RouterNames.firmwareUpgrad,
    E_RouterNames.firmwareUpgradList,
    E_RouterNames.appver,
    E_RouterNames.appverCreate,
    E_RouterNames.appArticles,
    E_RouterNames.appArticleAddEdit,
    E_RouterNames.appBanner,
    E_RouterNames.appBrief,
    E_RouterNames.ask,
    E_RouterNames.openAPI,
    E_RouterNames.largeScreenAdmin,
    E_RouterNames.dataDilution,
    E_RouterNames.deepseekSetting,
    E_RouterNames.platformScreenSetting,
    E_RouterNames.systemNoticeCreate,
    E_RouterNames.systemNoticeList,
    E_RouterNames.faultCaseLibraryList_root,
    E_RouterNames.faultCaseLibraryCreateEdit_root,

    E_RouterNames.diagnosticModel,
    E_RouterNames.diagnosticResult,
]

const CompanyAdminOnlyRouterNames = [
    E_RouterNames.customerManagement_operateLog,
    E_RouterNames.customerManagement_loginLog,
]

let hasInjectRouter = false

export function resetInjectRouter() {
    hasInjectRouter = false
}

function redirectSuperAdminRouter(router: VueRouter, store: Store<any>, to: Route, next) {
    if (store.getters.isSuperAdmin) {
        if (_.includes(SuperAdminRouterNames, to.name)) {
            return next()
        } else {
            return next({
                name: SuperAdminRouterNames[0]
            })
        }
    } else {
        if (to.matched.some(record => record.name === E_RouterNames.adminAlarmCenter)) {
            return next({ name: store.getters.defaultMenuRouterName })
        }
        if (_.includes(CompanyAdminOnlyRouterNames, to.name) && !store.getters.isAdmin) {
            return next({
                name: store.getters.defaultMenuRouterName
            })
        }
        if (to.path == '/__miss') {
            if (store.state.platformPortal?.loginEntry === E_PlatformPortalEntry.LargeScreen) {
                const largeScreenUrl = store.state.platformPortal?.serviceConfig?.largeScreenUrl?.trim()
                if (largeScreenUrl) {
                    window.location.replace(largeScreenUrl)
                    return
                }
                return next({ name: E_RouterNames.platformPortal })
            }
            return next({
                name: store.getters.loginEntryRouterName
            })
        }
        if (to.name === E_RouterNames.platformPortal && !store.state.platformPortal?.showPortal) {
            if (store.state.platformPortal?.loginEntry === E_PlatformPortalEntry.LargeScreen) {
                const largeScreenUrl = store.state.platformPortal?.serviceConfig?.largeScreenUrl?.trim()
                if (largeScreenUrl) {
                    window.location.replace(largeScreenUrl)
                    return
                }
            }
            const routeName = store.getters.loginEntryRouterName
            if (routeName && routeName !== E_RouterNames.platformPortal) {
                return next({ name: routeName })
            }
        }
        return next()
    }
}


export function makeRouter(store: Store<any>) {
    const router = new VueRouter({
        mode: 'hash',
        base: process.env.BASE_URL,
        routes: makeRoutes(store),
    });
    router.beforeEach((to, from, next) => {
        if (to.name === E_RouterNames.login) {
            return next()
        }

        let queryAccount = headerQueryAccount()
        if (!store.state.account && queryAccount) {
            return store.dispatch('autoLogin', queryAccount).then(() => {
                return redirectSuperAdminRouter(router, store, to, next)
            }).catch(err => {
                gVm.value.$message("Login Error")
            })
        }
        if (!store.state.account && autoLogin) {
            return store.dispatch('autoLogin', autoLoginUser).then(() => {
                return redirectSuperAdminRouter(router, store, to, next)
            }).catch(err => {
                gVm.value.$message("Login Error")
            })
        }

        if (store.state.account) {
            redirectSuperAdminRouter(router, store, to, next)
        } else {
            store.dispatch('fetchAccountInfoWithMenu').then(() => {
                redirectSuperAdminRouter(router, store, to, next)
            })
        }
    })
    router.beforeEach((to, from, next) => {
        if (to.meta?.keepDark) {
            themeRemoveLight()
        } else {
            themeInit()
        }
        next()
    })
    return router
}
